import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from 'lib/request/api';
import {
  ApiEnvelopeError,
  throwTypedEnvelopeError,
} from 'lib/evaluation-case/api-envelope';
import { calculatingInterval } from 'lib/evaluation-case/polling';
import {
  isShapeCarryingDomainKey,
  SCENARIO_DOMAINS,
  type ScenarioDomainKey,
} from 'lib/evaluation-case/scenario-domains';
import type { CaseFormValues } from '../types';

/** One scenario's footprint (EvaluationCase::ScenarioFootprints). */
export interface ScenarioFootprint {
  domain: ScenarioDomainKey;
  scenarioId: number;
  wellUwis: string[];
  targetSlotIds: number[];
  /**
   * The union of what its area configs pin; null when it pins no geometry, when nothing of it is
   * left on GeoJSON's nine-decimal grid, or overCap.
   */
  polygon: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
  /** Left out to keep the response under its 2 MB cap. */
  overCap: boolean;
}

/** `GET /evaluation_cases/scenario_footprints`. */
export type ScenarioFootprintsResponse =
  | { status: 'calculating'; pollIntervalMs?: number | null }
  | {
      status: 'ready';
      tolerance: number;
      footprints: ScenarioFootprint[];
    };

/** `{ domain key => scenario id }` for the coverage domains picked. */
export type ShapePicks = Partial<Record<ScenarioDomainKey, number>>;

/**
 * The picks the map draws: the coverage domains (the shape-carrying five and Type Curve) that have
 * a scenario, in registry order. Price Deck and DCA picks are left out; the endpoint would drop
 * them anyway.
 *
 * @param {object} scenarios - The form's picks, saved or not.
 * @return {ShapePicks} The coverage picks.
 */
export function shapePicks(scenarios: CaseFormValues['scenarios']): ShapePicks {
  const picks: ShapePicks = {};
  SCENARIO_DOMAINS.forEach((domain) => {
    const choice = scenarios[domain.key];
    if (domain.gatesCoverage && choice) picks[domain.key] = choice.id;
  });
  return picks;
}

/**
 * The picks the coverage preview takes: the shape-carrying ones. The Type Curve is the universe
 * the preview counts on (its typeCurveScenarioId), which it always gates on.
 *
 * @param {ShapePicks} picks - The drawn picks (shapePicks).
 * @return {ShapePicks} The shape-carrying picks among them.
 */
export function previewPicks(picks: ShapePicks): ShapePicks {
  return Object.fromEntries(
    Object.entries(picks).filter(([key]) => isShapeCarryingDomainKey(key))
  );
}

/**
 * The query string for a set of picks: `scenarios[taxes]=12&...`.
 *
 * @param {ShapePicks} picks - The picks.
 * @return {URLSearchParams} The params.
 */
export function picksSearchParams(picks: ShapePicks): URLSearchParams {
  const params = new URLSearchParams();
  Object.entries(picks).forEach(([key, id]) =>
    params.append(`scenarios[${key}]`, String(id))
  );
  return params;
}

export const FOOTPRINTS_QUERY_KEY = 'evaluation-case:footprints';

/**
 * Each picked coverage scenario's footprint, polled while the server builds them. Off when
 * nothing that draws one is picked.
 *
 * @param {ShapePicks} picks - The picks, unsaved ones included.
 * @return {object} The react-query result.
 */
export function useScenarioFootprints(picks: ShapePicks) {
  const params = picksSearchParams(picks);
  const key = params.toString();
  return useQuery<ScenarioFootprintsResponse, Error>({
    queryKey: [FOOTPRINTS_QUERY_KEY, key],
    queryFn: async ({ signal }) => {
      try {
        return await api
          .get('evaluation_cases/scenario_footprints', {
            searchParams: params,
            signal,
          })
          .json<ScenarioFootprintsResponse>();
      } catch (error) {
        return throwTypedEnvelopeError(
          error,
          ApiEnvelopeError,
          "Couldn't load the scenario areas"
        );
      }
    },
    enabled: key !== '',
    retry: false,
    staleTime: 300_000,
    // Keeps the last picks' footprints drawn while the next ones load.
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      calculatingInterval(query.state.data, query.state.status),
  });
}
